import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Phone } from "lucide-react";
import api from "@/lib/api";
import { resolveImageUrl, telHref } from "@/lib/site";
import { useSection } from "@/context/SiteContext";

const PHONE_TEL_FALLBACK = "tel:+4666051130";
const PHONE_FALLBACK = "0660-511 30";

const CATEGORY_ORDER = [
  "Ordinarie pizzor",
  "Salamipizzor",
  "Köttfärspizzor",
  "Havets pizzor",
  "Baconpizzor",
  "Vegetariska pizzor",
  "Mexicana pizzor",
  "Kycklingpizzor",
  "Amerikanska pizzor",
  "Inbakade pizzor",
  "Fläskfilépizzor (ubåt)",
  "Oxfilépizzor",
  "Biffpizzor",
  "Italienska pizzor - Vegetariana",
  "Italienska pizzor - Carne",
  "Nyhetspizza",
  "Rullar",
  "Kebab & kyckling",
  "Sallader",
  "Pasta",
  "Hamburgare",
  "Mexikanska rätter",
  "À la Carte",
  "Fiskrätter",
  "Barnmeny",
  "Familjepizzor",
  "Dryck & Tillbehör",
];

const CATEGORY_META = {
  "Ordinarie pizzor": { emoji: "🍕", img: "" },
  Salamipizzor: { emoji: "🌶️", img: "" },
  Köttfärspizzor: { emoji: "🥩", img: "" },
  "Havets pizzor": { emoji: "🦐", img: "" },
  Baconpizzor: { emoji: "🥓", img: "" },
  "Vegetariska pizzor": { emoji: "🌱", img: "" },
  "Mexicana pizzor": { emoji: "🌮", img: "" },
  Kycklingpizzor: { emoji: "🍗", img: "" },
  "Amerikanska pizzor": { emoji: "🇺🇸", img: "" },
  "Inbakade pizzor": { emoji: "🥟", img: "" },
  "Fläskfilépizzor (ubåt)": { emoji: "🥖", img: "" },
  Oxfilépizzor: { emoji: "🥩", img: "" },
  Biffpizzor: { emoji: "🍖", img: "" },
  "Italienska pizzor - Vegetariana": { emoji: "🇮🇹", img: "" },
  "Italienska pizzor - Carne": { emoji: "🇮🇹", img: "" },
  Nyhetspizza: { emoji: "✨", img: "" },
  Rullar: { emoji: "🌯", img: "" },
  "Kebab & kyckling": { emoji: "🔥", img: "" },
  Sallader: { emoji: "🥗", img: "" },
  Pasta: { emoji: "🍝", img: "" },
  Hamburgare: { emoji: "🍔", img: "" },
  "Mexikanska rätter": { emoji: "🌯", img: "" },
  "À la Carte": { emoji: "🍴", img: "" },
  Fiskrätter: { emoji: "🐟", img: "" },
  Barnmeny: { emoji: "🧒", img: "" },
  Familjepizzor: { emoji: "👨‍👩‍👧", img: "" },
  "Dryck & Tillbehör": { emoji: "🥤", img: "" },
};

export default function MenuSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const section = useSection("menu_section");
  const contact = useSection("contact");
  const tel = telHref(contact.phone_tel, contact.phone) || PHONE_TEL_FALLBACK;
  const phone = contact.phone || PHONE_FALLBACK;

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/menu");
        setItems(data);
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const grouped = items.reduce((acc, it) => {
    (acc[it.category] = acc[it.category] || []).push(it);
    return acc;
  }, {});
  const categories = CATEGORY_ORDER.filter((c) => grouped[c]?.length);

  return (
    <section id="meny" className="py-16 md:py-24 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="inline-block text-xs uppercase tracking-[0.22em] font-semibold text-[var(--primary)]">
            {section.eyebrow || "Vår meny"}
          </span>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mt-3">
            {section.title_line1 || "Lagat med kärlek,"}<br />
            <span className="italic font-medium">{section.title_italic || "serverat generöst"}</span>
          </h2>
        </div>

        {loading ? (
          <div className="text-center py-12 text-[var(--text-muted)]">Laddar meny…</div>
        ) : (
          <Accordion type="multiple" defaultValue={["Pizza"]} className="w-full">
            {categories.map((cat) => {
              const meta = CATEGORY_META[cat] || {};
              return (
                <AccordionItem
                  key={cat}
                  value={cat}
                  className="border-b border-[var(--border)]"
                  data-testid={`menu-accordion-${cat.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  <AccordionTrigger
                    data-testid={`menu-trigger-${cat.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="py-6 hover:no-underline group"
                  >
                    <div className="flex items-center gap-4 w-full text-left">
                      <span className="text-2xl">{meta.emoji}</span>
                      <span className="font-serif-display text-2xl sm:text-3xl font-bold tracking-tight group-hover:text-[var(--primary)] transition-colors">
                        {cat}
                      </span>
                      <span className="ml-auto mr-3 text-[11px] uppercase tracking-wider font-semibold text-[var(--text-muted)] bg-[var(--surface-alt)] rounded-full px-3 py-1">
                        {grouped[cat].length} rätter
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-8">
                    <ul className="divide-y divide-[var(--border)]/60">
                      {grouped[cat].map((it) => (
                        <li
                          key={it.id}
                          data-testid={`menu-item-${it.id}`}
                          className="flex items-start gap-4 py-4"
                        >
                          {it.image_url && (
                            <img
                              src={resolveImageUrl(it.image_url)}
                              alt={it.name}
                              className="w-16 h-16 rounded-xl object-cover shrink-0 border border-[var(--border)]"
                              loading="lazy"
                            />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-baseline gap-3">
                              <span className="font-semibold text-[var(--text)] text-base sm:text-lg">
                                {it.name}
                              </span>
                              <span className="flex-1 border-b border-dotted border-[var(--border)] translate-y-[-4px]" />
                              <span className="font-serif-display font-bold text-base sm:text-lg tabular-nums">
                                {Math.round(it.price)} kr
                              </span>
                            </div>
                            {it.description && (
                              <p className="text-sm text-[var(--text-muted)] mt-1">{it.description}</p>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        )}

        <div className="mt-12 rounded-3xl bg-[var(--surface-alt)] p-6 sm:p-8 text-center">
          <p className="text-[var(--text-muted)] text-lg mb-4">
            {section.cta_text || "Beställ direkt via telefon eller kom förbi oss"}
          </p>
          <a
            href={tel}
            data-testid="menu-cta-call"
            className="inline-flex items-center gap-2 rounded-full bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white px-7 py-4 font-semibold shadow-lg shadow-[var(--primary)]/20 transition-all hover:-translate-y-0.5"
          >
            <Phone size={18} /> Ring {phone}
          </a>
        </div>
      </div>
    </section>
  );
}
